import { motion } from "framer-motion";
import { ArrowRight, Activity } from "lucide-react";

const HERO_VIDEO =
  "/assets/4wer6qyy_Website Video(1).webm";

export const Hero = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden flex items-center"
    >
      {/* Background video */}
      <video
        data-testid="hero-bg-video"
        src={HERO_VIDEO}
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover scale-105"
      />

      {/* Layered overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/65 to-[#0A0A0C]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_40%,rgba(230,0,0,0.18),transparent_60%)]" />
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="absolute inset-0 noise" />

      {/* Scan line accent */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#E60000] to-transparent opacity-60" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

      {/* Floating UI corner tags */}
      <div className="hidden md:flex absolute top-28 right-8 lg:right-16 z-10 items-center gap-2 px-3.5 py-2 glass rounded-full">
        <Activity className="w-3.5 h-3.5 text-[#FF2400]" />
        <span className="font-mono text-[11px] tracking-[0.2em] text-white/80">
          SYS · LIVE 24/7
        </span>
        <span className="dot-pulse" />
      </div>

      <div className="hidden lg:block absolute bottom-32 right-12 z-10 w-72">
        <div className="glass rounded-2xl p-4 border-white/10">
          <div className="flex items-center justify-between mb-3">
            <span className="eyebrow-dim">PULSE · BLUDE NETWORK</span>
            <span className="dot-pulse" />
          </div>
          <svg viewBox="0 0 240 50" className="w-full h-12">
            <polyline
              fill="none"
              stroke="#FF2400"
              strokeWidth="1.5"
              points="0,25 20,25 30,10 40,40 50,5 60,45 70,25 90,25 100,18 115,32 130,25 160,25 170,8 180,42 195,25 240,25"
            />
            <polyline
              fill="none"
              stroke="rgba(255,36,0,0.25)"
              strokeWidth="6"
              points="0,25 20,25 30,10 40,40 50,5 60,45 70,25 90,25 100,18 115,32 130,25 160,25 170,8 180,42 195,25 240,25"
            />
          </svg>
          <div className="flex items-center justify-between mt-1 font-mono text-[10px] tracking-widest text-white/40">
            <span>00:00:42</span>
            <span>BPM 88 · SpO₂ 98%</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 w-full pt-32 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.8, ease: "easeOut" }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="dot-pulse" />
          <span className="eyebrow text-white/90">
            LIFE SCIENCE &middot; TECH &middot; EST. 2025
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.9, ease: "easeOut" }}
          className="font-display text-5xl sm:text-7xl lg:text-[112px] leading-[0.95] tracking-tighter font-medium text-white max-w-5xl"
          data-testid="hero-headline"
        >
          Bridging the gap
          <br />
          between{" "}
          <span className="italic font-light text-white/90">crisis</span>
          <br />
          and{" "}
          <span className="bg-gradient-to-r from-[#FF2400] to-[#FF6A4D] bg-clip-text text-transparent">
            survival.
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.8 }}
          className="mt-7 max-w-2xl text-base sm:text-lg text-white/70 leading-relaxed text-justify"
        >
          We create life-saving solutions for the moments that matter most -
          empowering healthcare providers and first responders with rapid,
          reliable interventions.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-3"
        >
          <button
            data-testid="hero-cta-pipeline"
            onClick={() => scrollTo("pipeline")}
            className="btn-primary"
          >
            View Pipeline <ArrowRight className="w-4 h-4" />
          </button>
          <button
            data-testid="hero-cta-science"
            onClick={() => scrollTo("science")}
            className="btn-ghost"
          >
            Our Science
          </button>
        </motion.div>

        {/* Stat strip */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="mt-20 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden max-w-4xl"
        >
          {[
            { k: "Pipeline", v: "3", s: "Candidates" },
            { k: "Platforms", v: "2", s: "LifeX · TechX" },
            { k: "Focus", v: "5", s: "Verticals" },
            { k: "Science", v: "3", s: "Core Stacks" },
          ].map((s) => (
            <div key={s.k} className="bg-[#0A0A0C]/80 px-5 py-5">
              <div className="eyebrow-dim">{s.k}</div>
              <div className="font-display text-3xl mt-1.5 text-white">
                {s.v}
              </div>
              <div className="text-xs text-white/50 mt-1">{s.s}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2">
        <div className="font-mono text-[10px] tracking-[0.3em] text-white/40">
          SCROLL
        </div>
        <div className="w-px h-10 bg-gradient-to-b from-white/50 to-transparent" />
      </div>
    </section>
  );
};

